import { ptBR } from '@/i18n/pt-BR';
import { isPapelConhecido, roleLabel } from '@/lib/roles';

/**
 * Detalhe do fato → frase, **por ação** (F-E61.F4-01). // ux-neutrality:allow doc-comment
 *
 * A versão anterior era genérica: virava `chave: valor` para qualquer objeto e // ux-neutrality:allow doc-comment
 * aplicava `String` nas transições. Como o detalhe é gravado pelo servidor com // ux-neutrality:allow doc-comment
 * o vocabulário INTERNO (`{"papel": "bank_user"}`), a tela publicava enum e // ux-neutrality:allow doc-comment
 * nome de campo do payload para o gestor — e o lint de neutralidade não pega, // ux-neutrality:allow doc-comment
 * porque só lê string estática. // ux-neutrality:allow doc-comment
 *
 * Agora quem sabe ler o detalhe é a AÇÃO: ela declara a espécie de cada valor, // ux-neutrality:allow doc-comment
 * e o valor sai traduzido pela fonte canônica (`roleLabel`, E52.F3). Ação ou // ux-neutrality:allow doc-comment
 * chave que este catálogo não conhece renderiza **vazio** — a ação já tem nome // ux-neutrality:allow doc-comment
 * próprio na coluna ao lado, e despejar payload para "não perder informação" é // ux-neutrality:allow doc-comment
 * exatamente como a violação entrou. // ux-neutrality:allow doc-comment
 */
type EspecieValor = 'papel' | 'acesso' | 'plataforma' | 'texto' | 'numero';

type CampoSimples = 'papel' | 'nome' | 'cnpj' | 'pessoas_desativadas' | 'casos_preservados';

// Quem NÃO é papel nem texto: as contagens do encerramento (E69.F1).
const _ESPECIE_DO_CAMPO: Partial<Record<CampoSimples, EspecieValor>> = {
  papel: 'papel',
  pessoas_desativadas: 'numero',
  casos_preservados: 'numero',
};

interface LeituraDoDetalhe {
  /** Como ler `de`/`para`, quando a ação registra uma transição. */
  transicao?: EspecieValor;
  /** Campos simples exibidos, na ordem — cada um com rótulo próprio. */
  campos?: readonly CampoSimples[];
}

const _LEITURA_POR_ACAO: Record<string, LeituraDoDetalhe> = {
  cliente_criado: { campos: ['nome', 'cnpj'] },
  // O vínculo guarda o NOME do cliente nos dois lados (e os ids ao lado, que
  // são de consulta, não de leitura humana).
  escritorio_vinculado: { transicao: 'texto' },
  escritorio_desvinculado: { transicao: 'texto' },
  escritorio_marcado_plataforma: { transicao: 'plataforma' },
  escritorio_desmarcado_plataforma: { transicao: 'plataforma' },
  escritorio_criado: { campos: ['papel'] },
  usuario_criado: { campos: ['papel'] },
  papel_alterado: { transicao: 'papel' },
  // Duas formas convivem: o console grava a transição do acesso; o banco e o
  // escritório gravam o papel de quem perdeu ou recuperou o acesso.
  acesso_desativado: { transicao: 'acesso', campos: ['papel'] },
  acesso_reativado: { transicao: 'acesso', campos: ['papel'] },
  // Só o id do documento — identificador interno, nada a mostrar.
  laudo_certificado_baixado: {},
  // Idem (E65.F6): id do documento e o grupo dele, ambos internos. O nome do
  // arquivo fica FORA da trilha de propósito — é texto do cliente e traz PII.
  documento_baixado: {},
  // E69.F1 (ADR-0072): o encerramento fecha a conta do ambiente; a reabertura
  // não carrega detalhe porque não reativa ninguém. O `acesso_desativado` de
  // cada pessoa do escritório encerrado traz `motivo`, que é chave interna e
  // fica fora da leitura — a ação do escritório, ao lado, já diz o porquê.
  escritorio_encerrado: { campos: ['pessoas_desativadas', 'casos_preservados'] },
  escritorio_reaberto: {},
};

const _ROTULO_DO_CAMPO: Record<CampoSimples, string> = {
  papel: ptBR.trilha.campoPapel,
  nome: ptBR.trilha.campoNome,
  cnpj: ptBR.trilha.campoCnpj,
  pessoas_desativadas: ptBR.trilha.campoPessoasDesativadas,
  casos_preservados: ptBR.trilha.campoCasosPreservados,
};

const _FORMATADOR: Record<EspecieValor, (bruto: unknown) => string> = {
  papel: (bruto) => (isPapelConhecido(bruto) ? roleLabel(bruto) : ptBR.trilha.valorAusente),
  acesso: (bruto) => (bruto === true ? ptBR.trilha.acessoAtivo : ptBR.trilha.acessoSem),
  plataforma: (bruto) => (bruto === true ? ptBR.trilha.plataformaSim : ptBR.trilha.plataformaNao),
  texto: (bruto) => (typeof bruto === 'string' ? bruto : ptBR.trilha.valorAusente),
  numero: (bruto) => (typeof bruto === 'number' ? String(bruto) : ptBR.trilha.valorAusente),
};

function _valor(especie: EspecieValor, bruto: unknown): string {
  if (bruto === null || bruto === undefined) return ptBR.trilha.valorAusente;
  return _FORMATADOR[especie](bruto);
}

/** `detalhe` do evento → frase curta, ou vazio quando não há o que dizer. */
export function detalheLegivel(acao: string, detalhe: Record<string, unknown>): string {
  const leitura = _LEITURA_POR_ACAO[acao];
  if (leitura === undefined) return '';

  const partes: string[] = [];
  const temTransicao = leitura.transicao !== undefined && ('de' in detalhe || 'para' in detalhe);
  if (temTransicao && leitura.transicao !== undefined) {
    const de = _valor(leitura.transicao, detalhe['de']);
    const para = _valor(leitura.transicao, detalhe['para']);
    partes.push(`${de} → ${para}`);
  }
  for (const campo of leitura.campos ?? []) {
    if (!(campo in detalhe)) continue;
    const especie: EspecieValor = _ESPECIE_DO_CAMPO[campo] ?? 'texto';
    partes.push(`${_ROTULO_DO_CAMPO[campo]}: ${_valor(especie, detalhe[campo])}`);
  }
  return partes.join(' · ');
}
